/**
 * ProsumerResultsSummary - Summary card for prosumer calculation results.
 *
 * Color-coded: green (>10% savings), yellow (0-10%), red (negative).
 * Shows annual savings, recommended tariff, self-consumption ratio.
 */

import { Card, Text, Group, Stack, Badge, ThemeIcon } from '@mantine/core';
import { IconTrendingUp, IconTrendingDown, IconMinus } from '@tabler/icons-react';
import type { ProsumerCalculationResult } from '../../types';

export interface ProsumerResultsSummaryProps {
  result: ProsumerCalculationResult;
}

function getSavingsColor(percent: number): string {
  if (percent > 10) return 'green';
  if (percent >= 0) return 'yellow';
  return 'red';
}

function getSavingsIcon(percent: number) {
  if (percent > 10) return <IconTrendingUp size={20} />;
  if (percent >= 0) return <IconMinus size={20} />;
  return <IconTrendingDown size={20} />;
}

export function ProsumerResultsSummary({ result }: ProsumerResultsSummaryProps) {
  const color = getSavingsColor(result.maxSavingsPercent);

  return (
    <Card withBorder radius="md" p="lg" style={{ borderColor: `var(--mantine-color-${color}-4)` }}>
      <Stack gap="sm">
        <Group justify="space-between">
          <Group gap="xs">
            <ThemeIcon color={color} variant="light" size="lg" radius="xl">
              {getSavingsIcon(result.maxSavingsPercent)}
            </ThemeIcon>
            <div>
              <Text fw={600} size="lg" data-testid="savings-amount">
                {result.maxSavingsPln > 0 ? '+' : ''}{Math.round(result.maxSavingsPln)} PLN/rok
              </Text>
              <Text size="xs" c="dimmed">Maksymalne oszczędności</Text>
            </div>
          </Group>
          <Badge color={color} size="lg" variant="light" data-testid="savings-badge">
            {(result.maxSavingsPercent ?? 0) > 0 ? '+' : ''}{(result.maxSavingsPercent ?? 0).toFixed(1)}%
          </Badge>
        </Group>

        <Group justify="space-between">
          <div>
            <Text size="sm" c="dimmed">Rekomendowana taryfa</Text>
            <Text fw={500} data-testid="recommended-tariff">{result.recommendedTariff}</Text>
          </div>
          <div>
            <Text size="sm" c="dimmed">Autokonsumpcja</Text>
            <Text fw={500} data-testid="self-consumption-ratio">
              {result.selfConsumption
                ? `${(result.selfConsumption.effectiveRatio * 100).toFixed(0)}%`
                : '–'}
            </Text>
          </div>
        </Group>
      </Stack>
    </Card>
  );
}
